import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { useGetSystemJobQuery } from '../../../../../queries';
import { baseStyleSheet } from '../../../../styles/styles';
import { LoadingSpinner } from '../../../LoadingSpinner';
import { ContactsImportProcessingIcon } from '../../../svgs/icons/ContactsImportProcessingIcon';
import { SuccessCheckIcon } from '../../../svgs/icons/SuccessCheckIcon';
import { styleSheet } from './styles';

const parseImportWarnings = (systemJob?: Api.ISystemJob) => {
	const raw = systemJob?.additionalFields?.importWarnings;
	if (!raw) {
		return [];
	}
	try {
		const parsed: unknown = JSON.parse(raw);
		return Array.isArray(parsed) ? parsed.filter((entry): entry is string => typeof entry === 'string') : [];
	} catch {
		return [];
	}
};

export const ProcessStep = ({
	systemJob,
	setSystemJob,
	singularEntity,
	pluralEntity,
	onComplete,
	completeButtonLabel,
}: {
	systemJob: Api.ISystemJob;
	setSystemJob: React.Dispatch<React.SetStateAction<Api.ISystemJob>>;
	singularEntity: string;
	pluralEntity: string;
	onComplete?: () => void;
	completeButtonLabel?: string;
}) => {
	const isJobComplete = systemJob.percentComplete >= 100;
	const isCompleteAndFailed = systemJob.percentComplete === -1;
	const isInProgress = !isJobComplete && !isCompleteAndFailed;
	const bySupportUser = false;

	const getSystemJobQuery = useGetSystemJobQuery({
		enabled: !!systemJob,
		jobId: systemJob.id,
		refetchInterval: query => {
			const data = query.state.data;
			if (!systemJob.id) {
				return false;
			}
			return !(data?.percentComplete >= 100) ? 3000 : false;
		},
		refetchOnWindowFocus: false,
	});

	React.useEffect(() => {
		if (!getSystemJobQuery.data) {
			return;
		}
		setSystemJob(getSystemJobQuery.data);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [getSystemJobQuery.data]);

	const queryData = getSystemJobQuery.data;
	const processed = queryData?.recordsProcessed || 0;
	const succeeded = queryData?.recordsSucceeded ?? queryData?.recordsProcessed ?? 0;
	const progressMessage =
		queryData?.status ||
		(systemJob ? `Processed ${processed} ${processed > 1 || processed === 0 ? pluralEntity : singularEntity}` : '');
	const completionMessage =
		queryData?.status || `Imported ${succeeded} ${succeeded === 1 ? singularEntity : pluralEntity}`;
	const importWarnings = isJobComplete ? parseImportWarnings(queryData ?? systemJob) : [];

	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.icon)}>
				<ContactsImportProcessingIcon />
				{!!isJobComplete && <SuccessCheckIcon className={css(styleSheet.iconSpinner, styleSheet.iconSuccess)} />}
				{isInProgress && <LoadingSpinner className={css(styleSheet.iconSpinner)} type='small' />}
			</div>
			<div className={css(styleSheet.message)}>
				{isJobComplete ? completionMessage : !isCompleteAndFailed ? progressMessage : 'Import failed.'}
			</div>
			<div className={css(styleSheet.detail)}>
				{isJobComplete
					? `We finished importing ${bySupportUser ? 'the' : 'your'} ${pluralEntity}.`
					: !isCompleteAndFailed
						? 'We will email you when we are done. You can close this window now.'
						: `Failed to import all ${pluralEntity}: ${systemJob.status}`}
			</div>
			{importWarnings.length > 0 ? (
				<div className={css(styleSheet.warnings)}>
					{importWarnings.map(warning => (
						<div className={css(styleSheet.warningItem)} key={warning}>
							{warning}
						</div>
					))}
				</div>
			) : null}
			{isJobComplete && onComplete ? (
				<button className={css(baseStyleSheet.ctaButton, styleSheet.closeButton)} onClick={onComplete}>
					{completeButtonLabel || 'Done'}
				</button>
			) : null}
		</div>
	);
};
